import React, { useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import LockIcon from '@material-ui/icons/LockOutlined'
import {
  headlessStoreApiKeys,
  headlessApiKeyStatus,
  headlessDeleteApiKey,
} from 'src/api/headless'
import styles from './styles'

const KeyRow = ({ label, isSet, value, onChange, onRemove, placeholder, classes, loading }) => (
  <Box mb={3}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
        {label}
      </Typography>
      {isSet && (
        <div className={classes.statusBadge}>
          <CheckCircleIcon style={{ color: '#10B981', fontSize: '16px' }} />
          <Typography className={classes.activeText}>ACTIVE</Typography>
          <Tooltip title="Remove Key">
            <IconButton size="small" onClick={onRemove} style={{ marginLeft: '4px', padding: '2px' }}>
              <DeleteIcon style={{ fontSize: '16px', color: '#EF4444' }} />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </Box>
    <TextField
      fullWidth
      type="password"
      variant="outlined"
      size="small"
      placeholder={isSet ? '••••••••••••••••••••••••••••' : placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={classes.input}
      disabled={loading}
    />
  </Box>
)

const SettingsPanel = ({ classes }) => {
  const [geminiKey, setGeminiKey] = useState('')
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchStatus = async () => {
    try {
      const res = await headlessApiKeyStatus()
      const data = res?.data || res
      setStatus(data?.keys || data)
    } catch (e) {
      console.error('Failed to fetch BYOK status:', e)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const handleSaveKeys = async () => {
    setLoading(true)
    try {
      const keys = {}
      if (geminiKey) keys.google_api_key = geminiKey

      await headlessStoreApiKeys(keys)
      setGeminiKey('')
      await fetchStatus()
    } catch (e) {
      console.error('Failed to save keys:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveKey = async type => {
    try {
      await headlessDeleteApiKey(type)
      await fetchStatus()
    } catch (e) {
      console.error(`Failed to remove key ${type}:`, e)
    }
  }

  const checkIsSet = (keyName) => {
    if (!status) return false
    return status[keyName] === true || (typeof status[keyName] === 'string' && status[keyName].length > 0)
  }

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <LockIcon style={{ color: '#3B82F6', marginRight: '16px' }} />
          <Typography style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
            Gemini Translator API Key (BYOK)
          </Typography>
        </Box>
        <Typography style={{ fontSize: '14px', color: '#64748B', marginBottom: '28px', lineHeight: 1.5 }}>
          Learning paths are translated using Google Gemini. Add your own API key to bypass rate limits.
        </Typography>

        <Box display="flex" flexDirection="column">
          <KeyRow
            label="Gemini API Key"
            isSet={checkIsSet('google_api_key')}
            value={geminiKey}
            onChange={setGeminiKey}
            onRemove={() => handleRemoveKey('google_api_key')}
            placeholder="AIza..."
            classes={classes}
            loading={loading}
          />

          <Box mt={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || !geminiKey}
              onClick={handleSaveKeys}
              className={classes.button}
              style={{ padding: '12px 32px', minWidth: '160px' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Save API Key'}
            </Button>
          </Box>
        </Box>
      </div>
    </div>
  )
}


export default withStyles(styles)(SettingsPanel)
